/** @format */

import {useState} from 'react';

import {Box, Typography} from '@mui/material';

import {Trans} from 'react-i18next';

import Breadcrumbs from 'components/Breadcrumbs';
import {RecommendationElement} from 'components/RecommendationElement';
import RSAModalNew from 'components/RSAModalNew';
import {ROUTES} from 'constants/ROUTES';
import {recommendationName} from 'helpers/recommendation_names';

import {ReportButton, Title, TitleActions, TitleBox, Wrapper} from './styles';
import {
  useCreateRecommendationSuspendedMutation,
  useDeleteRecommendationSuspendedMutation,
  useGetRecommendationsQuery,
  useGetSuspendedRecommendationsQuery,
} from '../Project/components/Recommendation/graphql/__generated__';
import {GET_SUSPENDED_RECOMMENDATIONS} from '../Project/components/Recommendation/graphql/recommendation.queries';
import {useGetCurrentUserQuery} from './graphql/__generated__';

const ProjectReports = () => {
  const {
    data: currentUserData,
    loading: currentUserLoading,
    error: currentUserError,
  } = useGetCurrentUserQuery();
  const {
    data: recommendationsData,
    loading: recommendationsLoading,
    error: recommendationsError,
  } = useGetRecommendationsQuery();
  const {data: suspendedRecommendations} = useGetSuspendedRecommendationsQuery();

  const [deleteRecommendationFromSuspends] = useDeleteRecommendationSuspendedMutation({
    refetchQueries: [GET_SUSPENDED_RECOMMENDATIONS],
  });

  const [suspendRecommendation] = useCreateRecommendationSuspendedMutation({
    refetchQueries: [GET_SUSPENDED_RECOMMENDATIONS],
  });

  const [selectedRecommendation, setSelectedRecommendation] = useState<string | null>(null);

  const handleRecommendationClick = (name: string) => {
    setSelectedRecommendation(name);
  };

  const handleModalClose = () => {
    setSelectedRecommendation(null);
  };

  const breadcrumbs = [
    {
      href: ROUTES.INDEX,
      title: <Trans>Проекты</Trans>,
    },
    {
      href: `/${ROUTES.HOME}`,
      title: <Trans>{currentUserData?.currentUser?.currentCompany?.name}</Trans>,
    },
    {
      href: '',
      title: <Trans>Рекомендации</Trans>,
    },
  ];

  const suspendedRecommendationsResult =
    suspendedRecommendations?.currentUser?.currentCompany?.recommendationSuspendeds;
  const recommendationsResult =
    recommendationsData?.currentUser?.currentCompany?.lastRecommendations?.filter(
      ({found}) => found > 0,
    ) || [];

  const handleRemoveFromSuspends = (name: string) => {
    const id = suspendedRecommendationsResult?.find(
      ({sensorName}: {sensorName: string}) => sensorName === name,
    )?.id;

    if (id) {
      deleteRecommendationFromSuspends({variables: {id}});
    }
  };

  const checkIsSuspended = (name: string) => {
    return suspendedRecommendationsResult?.some(
      ({sensorName}: {sensorName: string}) => sensorName === name,
    );
  };

  const handleSuspendAll = () => {
    if (recommendationsResult?.length) {
      recommendationsResult.forEach(({sensor: {name}}) => {
        suspendRecommendation({variables: {input: {sensorName: name}}});
      });
    }
  };

  const handleUnsuspendAll = () => {
    if (suspendedRecommendationsResult?.length) {
      suspendedRecommendationsResult.forEach(({id}: {id: string}) => {
        deleteRecommendationFromSuspends({variables: {id}});
      });
    }
  };

  if (currentUserLoading || recommendationsLoading) {
    return (
      <Typography>
        <Trans>Загрузка</Trans>
      </Typography>
    );
  }
  if (currentUserError || recommendationsError) {
    return (
      <Typography>
        <Trans>Ошибка при загрузке данных</Trans>
      </Typography>
    );
  }

  return (
    <Box>
      <Breadcrumbs items={breadcrumbs} />
      <TitleBox>
        <Title>
          <Trans>Рекомендации</Trans>
        </Title>
        <TitleActions>
          <ReportButton onClick={handleSuspendAll}>Все прочитаны</ReportButton>
          <ReportButton onClick={handleUnsuspendAll}>Все не прочитаны</ReportButton>
        </TitleActions>
      </TitleBox>
      <Wrapper>
        {recommendationsResult?.map(({sensor: {name}, found, id}) => (
          <RecommendationElement
            key={id}
            number={found}
            text={recommendationName(name)}
            onRemoveFromSuspends={
              checkIsSuspended(name) ? () => handleRemoveFromSuspends(name) : undefined
            }
            onClick={() => handleRecommendationClick(name)}
          />
        ))}
      </Wrapper>

      {selectedRecommendation && (
        <RSAModalNew
          onClose={handleModalClose}
          type={selectedRecommendation}
          title={recommendationName(selectedRecommendation)}
        />
      )}
    </Box>
  );
};

export default ProjectReports;
